import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Icon,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {
    useAutorizarOcorrenciaHomologacao,
    useDetalheOcorrencia,
    useCancelarPorCodigoRegistro,
    useCancelarPorChavePacote,
    useConfiguracaoEntrega,
    useReconciliarEntregaFornecedor
} from "../../api/DpgeIntegracaoEsfingeApi";

import ArquivarButton from "../../components/arquivar/ArquivarButton";
import DataPanel from "../../components/data/DataPanel";
import LabelEstado from "../../components/estado/LabelEstado";
import JsonPanel from "../../components/json-panel/JsonPanel";
import Page from "../../components/layout/page/Page";
import PageHeader from "../../components/page-header/PageHeader";
import Voltar from "../../components/voltar/Voltar";
import DetalhesOcorrenciaTabs from "../../components/ocorrencia/DetalhesOcorrenciaTabs";
import ErrosProcessamento from "../../components/ocorrencia/ErrosProcessamento";
import ResumoFolhaCompetencia from "../../components/ocorrencia/ResumoFolhaCompetencia";
import {getReferenciaTemporalOcorrencia} from "../../shared/ocorrenciaTemporalUtil";
import PessoasOcorrencia, {
    eventoPossuiPessoas
} from "../../components/ocorrencia/PessoasOcorrencia";
import FornecedorEntregaCard from "../../components/ocorrencia/FornecedorEntregaCard";
import {
    EtapasFluxo,
    ResumoProcessamento
} from "../../components/ocorrencia/ResumoProcessamento";

import {getDataGeracao, getTitulo} from "../../shared/ocorrenciaUtil";
import {useDialog} from "muibox";
import {useLoading} from "../../components/loading/LoadingProvider";
import useFeedback from "../../components/feedback/useFeedback";
import {
    AMBIENTE,
    GESTAO_FISCAL_ENVIO_HOMOLOGACAO_ENABLED
} from "../../shared/env";
import {formataDataTempoLong} from "../../shared/dataUtil";
import {
    canAuthorizeFinanceira,
    canAuthorizeFolha,
    canReconcileFornecedor
} from "../../shared/keycloak";

import {
    obterModoEnvio,
    ambientePermiteAutorizacao
} from "../../shared/modoAplicacao";
export {ambientePermiteAutorizacao} from "../../shared/modoAplicacao";

const ESTADO_AGUARDANDO_APROVACAO = "AGUARDANDO_APROVACAO";
const EVENTOS_CANCELAMENTO = new Set([
    "CANCELAR_TRANSMISSAO_POR_CODIGO_REGISTRO",
    "CANCELAR_TRANSMISSAO_ENVIO_PARCIAL"
]);
const EVENTOS_FINANCEIROS_AUTORIZAVEIS = new Set([
    "CONTAS_BANCARIAS",
    "GESTAO_FISCAL_ENVIAR",
    "CANCELAR_TRANSMISSAO_POR_CODIGO_REGISTRO",
    "CANCELAR_TRANSMISSAO_ENVIO_PARCIAL"
]);

function EventoDetalhePage() {
    const {id} = useParams();
    const navigate = useNavigate();
    const dialog = useDialog();
    const setLoading = useLoading();
    const {showError, showInfo, showSuccess} = useFeedback();
    const [justificativaAprovacao, setJustificativaAprovacao] = useState("");
    const [referenciaRemessaContabil, setReferenciaRemessaContabil] =
        useState("");

    const {
        data: ocorrencia = {},
        isLoading,
        refetch: refetchOcorrencia
    } = useDetalheOcorrencia(id);
    const configuracaoQuery = useConfiguracaoEntrega();
    const configuracaoEntrega = configuracaoQuery.data;
    const {data: ocorrenciaRaiz, isLoading: carregandoOcorrenciaRaiz} =
        useDetalheOcorrencia(ocorrencia.ocorrenciaPaiId, {
            enabled: Boolean(ocorrencia.ocorrenciaPaiId)
        });
    const {
        data: ocorrenciaOrigemCancelamento,
        isLoading: carregandoOrigemCancelamento
    } = useDetalheOcorrencia(ocorrencia.ocorrenciaOrigemCancelamentoId, {
        enabled: Boolean(ocorrencia.ocorrenciaOrigemCancelamentoId)
    });

    const ocorrenciaFluxo = ocorrenciaRaiz || ocorrencia;
    const auditoriaEfetiva = possuiAuditoriaHomologacao(ocorrencia)
        ? ocorrencia
        : ocorrenciaRaiz;
    const dadosTecnicos =
        ocorrencia.dadosEnviados ||
        (ocorrencia.ocorrenciaPaiId ? ocorrenciaRaiz?.dadosEnviados : null);

    const cancelarPorCR = useCancelarPorCodigoRegistro();
    const cancelarPorCP = useCancelarPorChavePacote();
    const autorizarHomologacao = useAutorizarOcorrenciaHomologacao();
    const reconciliarFornecedor = useReconciliarEntregaFornecedor();

    const destinoEntrega = obterDestinoEntrega(ocorrencia, ocorrenciaRaiz);
    const ehEntregaFornecedor = destinoEntrega === "FORNECEDOR";

    const ehCancelamento = EVENTOS_CANCELAMENTO.has(ocorrencia.eventoAtual);
    const cancelamentoConcluido = ocorrencia.cancelamentoConcluido;
    const eventosBloqueados = [...EVENTOS_CANCELAMENTO];

    /**
     * ===== REGRAS DE NEGÓCIO =====
     */
    const podeCancelarPorCR = () =>
        !cancelamentoConcluido &&
        !ehEntregaFornecedor &&
        !eventosBloqueados.includes(ocorrencia.eventoAtual) &&
        Boolean(ocorrencia.codigoRegistroInformacao) &&
        ocorrencia.estadoEnvio === "PROCESSADO_COM_SUCESSO" &&
        (ocorrencia.estrategiaEnvio === "INDIVIDUAL" ||
            (ocorrencia.estrategiaEnvio === "FLUXO" &&
                ocorrencia.eventoAtual?.includes("FINALIZAR")));

    const descarteFuncionalDisponivel =
        !cancelamentoConcluido &&
        podeDescartarPacoteFuncionalComErro(ocorrencia, destinoEntrega);

    const podeCancelarPorCP = () =>
        !cancelamentoConcluido &&
        !ehEntregaFornecedor &&
        !eventosBloqueados.includes(ocorrencia.eventoAtual) &&
        Boolean(ocorrencia.chavePacote) &&
        ocorrencia.estadoEnvio === "PROCESSADO_COM_SUCESSO" &&
        (ocorrencia.estrategiaEnvio === "INDIVIDUAL" ||
            (ocorrencia.estrategiaEnvio === "FLUXO" &&
                ocorrencia.eventoAtual?.includes("INICIAR")));

    const exclusaoPorCodigoDisponivel = podeCancelarPorCR();
    const exclusaoPorPacoteDisponivel =
        podeCancelarPorCP() || descarteFuncionalDisponivel;

    function motivoExclusaoIndisponivel() {
        if (ehEntregaFornecedor) {
            return "O cancelamento de entregas ao fornecedor ainda não está habilitado. A operação depende do contrato de integração e nunca utiliza protocolo do fornecedor como identificador do TCE-MS.";
        }
        if (eventosBloqueados.includes(ocorrencia.eventoAtual)) {
            return "Esta ocorrência já representa uma solicitação de cancelamento.";
        }
        if (ocorrencia.estadoEnvio === "RESULTADO_INDETERMINADO") {
            return "Consulte e reconcilie o resultado oficial antes de preparar um cancelamento ou repetir o envio.";
        }
        if (ocorrencia.estadoEnvio !== "PROCESSADO_COM_SUCESSO") {
            return "O cancelamento exige um envio aceito. O descarte de uma tentativa funcional com erro exige a chave do pacote, sem código de registro, na ocorrência inicial.";
        }
        if (!ocorrencia.codigoRegistroInformacao && !ocorrencia.chavePacote) {
            return "A ocorrência não possui código de registro nem chave de pacote para identificar o envio no e-Sfinge.";
        }
        return "Esta etapa do fluxo não permite exclusão. Em fluxos, a chave do pacote é usada na etapa inicial e o código de registro na etapa final.";
    }

    /**
     * ===== HANDLERS =====
     */
    async function confirmarEExecutar(acao) {
        await dialog.confirm({
            title: descarteFuncionalDisponivel
                ? "Deseja preparar o descarte deste pacote com erro?"
                : "Deseja preparar o cancelamento desta ocorrência?",
            message:
                "Uma nova ocorrência de cancelamento será preparada para revisão e autorização. A preparação preserva o histórico e ainda não transmite o cancelamento.",
            ok: {text: "CONFIRMAR", color: "primary"},
            cancel: {text: "CANCELAR", color: "primary"}
        });

        setLoading(true);
        acao();
    }

    function handleCancelarPorCR() {
        confirmarEExecutar(() =>
            cancelarPorCR.mutate(ocorrencia.id, {
                onError: showError,
                onSettled: () => {
                    setLoading(false);
                    navigate("/");
                }
            })
        );
    }

    function handleCancelarPorCP() {
        confirmarEExecutar(() =>
            cancelarPorCP.mutate(ocorrencia.id, {
                onError: showError,
                onSuccess: ({data}) => navigate(`/eventos/${data.id}`),
                onSettled: () => setLoading(false)
            })
        );
    }

    async function handleAutorizarHomologacao() {
        const justificativa = justificativaAprovacao.trim();

        if (
            !podeAutorizarOcorrencia(
                ocorrencia,
                AMBIENTE,
                podeAutorizarModulo(ocorrencia)
            )
        ) {
            showError(
                "Esta ocorrência não pode ser autorizada no estado ou ambiente atual."
            );
            await refetchOcorrencia();
            return;
        }

        if (!justificativaAprovacaoValida(justificativa)) {
            showError(
                "Informe uma justificativa entre 10 e 500 caracteres para autorizar o processamento."
            );
            return;
        }

        const referenciaRemessaContabilAnterior =
            referenciaRemessaContabil.trim();
        if (
            ocorrencia.eventoAtual === "CONTAS_BANCARIAS" &&
            !referenciaRemessaContabilValida(referenciaRemessaContabilAnterior)
        ) {
            showError(
                "Informe a referência que comprova a remessa de Registros Contábeis da competência anterior."
            );
            return;
        }

        try {
            await dialog.confirm({
                title: "Autorizar processamento em homologação?",
                message: `A ocorrência #${ocorrencia.id} será liberada para a fila de homologação. O scheduler poderá processá-la após esta confirmação. Esta ação não autoriza envio para produção. Justificativa: ${justificativa}`,
                ok: {
                    text: "AUTORIZAR EM HOMOLOGAÇÃO",
                    color: "warning"
                },
                cancel: {text: "CANCELAR", color: "primary"}
            });
        } catch (error) {
            return;
        }

        setLoading(true);
        try {
            await autorizarHomologacao.mutateAsync({
                id: ocorrencia.id,
                moduloOrigem: ocorrencia.moduloOrigem,
                justificativa,
                referenciaRemessaContabilAnterior
            });
            await refetchOcorrencia();
            setJustificativaAprovacao("");
            setReferenciaRemessaContabil("");
            showSuccess(
                "Autorização registrada. A ocorrência foi liberada para a fila de homologação."
            );
        } catch (error) {
            if (isConflito(error)) {
                await refetchOcorrencia();
                showInfo(
                    "A ocorrência já foi alterada por outra operação. Os dados foram atualizados."
                );
            } else {
                showError(error);
            }
        } finally {
            setLoading(false);
        }
    }

    async function handleReconciliarFornecedor(dados) {
        const descricoes = {
            CONFIRMAR_ACEITE: "confirmar o aceite pelo fornecedor",
            CONFIRMAR_REJEICAO: "confirmar a rejeição pelo fornecedor",
            REENFILEIRAR_NAO_RECEBIDA:
                "confirmar o não recebimento e reenfileirar a mesma entrega"
        };
        try {
            await dialog.confirm({
                title: "Confirmar reconciliação administrativa?",
                message: `Você irá ${
                    dados.codigoRegistroInformacao
                        ? "registrar a evidência de aceite oficial do TCE-MS deste pacote"
                        : descricoes[dados.acao]
                }. A decisão e os comprovantes informados serão auditados.`,
                ok: {text: "CONFIRMAR", color: "warning"},
                cancel: {text: "CANCELAR", color: "primary"}
            });
        } catch (error) {
            return;
        }

        setLoading(true);
        try {
            await reconciliarFornecedor.mutateAsync({
                ocorrenciaId: ocorrencia.id,
                ...dados
            });
            await refetchOcorrencia();
            showSuccess("Reconciliação registrada com auditoria.");
        } catch (error) {
            showError(error);
        } finally {
            setLoading(false);
        }
    }

    async function copiar(valor, label) {
        try {
            await navigator.clipboard.writeText(`${valor}`);
            showInfo(`${label} copiada`);
        } catch (error) {
            showError("Não foi possível copiar o valor.");
        }
    }

    const quantidadeEtapas =
        (ocorrenciaFluxo.ocorrenciasFilhas?.length || 0) + 1;
    const possuiEtapas = quantidadeEtapas > 1;
    const possuiAuditoria = possuiAuditoriaHomologacao(auditoriaEfetiva);
    const possuiDadosTecnicos = Boolean(dadosTecnicos || ocorrencia.resposta);
    const secoesComplementares = [
        eventoPossuiPessoas(ocorrencia.eventoAtual) && {
            id: "pessoas",
            rotulo: "Pessoas e valores",
            icone: "people",
            conteudo: (
                <PessoasOcorrencia
                    key={ocorrencia.id}
                    ocorrenciaId={ocorrencia.id}
                />
            )
        },
        possuiEtapas && {
            id: "etapas",
            rotulo: `Etapas (${quantidadeEtapas})`,
            icone: "account_tree",
            conteudo: (
                <EtapasFluxo
                    ocorrencia={ocorrencia}
                    ocorrenciaRaiz={ocorrenciaRaiz}
                    onNavigate={(ocorrenciaId) =>
                        navigate(`/eventos/${ocorrenciaId}`)
                    }
                    embedded
                />
            )
        },
        possuiAuditoria && {
            id: "auditoria",
            rotulo: "Auditoria",
            icone: "verified_user",
            conteudo: (
                <AuditoriaHomologacaoCard
                    ocorrencia={auditoriaEfetiva}
                    ocorrenciaOrigemId={
                        auditoriaEfetiva.id !== ocorrencia.id
                            ? auditoriaEfetiva.id
                            : undefined
                    }
                    embedded
                />
            )
        },
        possuiDadosTecnicos && {
            id: "dados-tecnicos",
            rotulo: "Dados técnicos",
            icone: "data_object",
            conteudo: (
                <Stack spacing={2}>
                    {dadosTecnicos && (
                        <JsonPanel
                            primary={
                                ocorrencia.dadosEnviados
                                    ? getRotulosPayloadPorEstado(
                                          ocorrencia.estadoEnvio
                                      ).primary
                                    : "Dados técnicos do fluxo"
                            }
                            secondary={
                                ocorrencia.dadosEnviados
                                    ? getRotulosPayloadPorEstado(
                                          ocorrencia.estadoEnvio
                                      ).secondary
                                    : `Payload registrado na ocorrência raiz #${ocorrenciaRaiz?.id} e utilizado por esta etapa técnica`
                            }
                            src={dadosTecnicos}
                        />
                    )}

                    {ocorrencia.resposta && (
                        <JsonPanel
                            primary={
                                ehEntregaFornecedor
                                    ? "Resposta do fornecedor"
                                    : "Resposta do e-Sfinge"
                            }
                            secondary={
                                ehEntregaFornecedor
                                    ? "Retorno integral da etapa contratada; não confirma aceite do TCE-MS"
                                    : "Retorno integral associado ao processamento"
                            }
                            src={ocorrencia.resposta}
                        />
                    )}
                </Stack>
            )
        }
    ].filter(Boolean);

    return (
        <Page>
            <DataPanel
                loading={
                    isLoading ||
                    (Boolean(ocorrencia.ocorrenciaPaiId) &&
                        carregandoOcorrenciaRaiz) ||
                    (Boolean(ocorrencia.ocorrenciaOrigemCancelamentoId) &&
                        carregandoOrigemCancelamento)
                }
            >
                <PageHeader
                    primary={getTitulo(ocorrencia)}
                    secondary={getDataGeracao(ocorrencia.dataHora)}
                    commands={[
                        ocorrencia.ocorrenciaPaiId ? (
                            <Button
                                key="raiz"
                                variant="outlined"
                                startIcon={<Icon>account_tree</Icon>}
                                onClick={() =>
                                    navigate(
                                        `/eventos/${ocorrencia.ocorrenciaPaiId}`
                                    )
                                }
                            >
                                Ver ocorrência raiz
                            </Button>
                        ) : undefined,
                        ocorrencia.ocorrenciaOrigemCancelamentoId ? (
                            <Button
                                key="origem-cancelamento"
                                variant="outlined"
                                color="error"
                                startIcon={<Icon>link</Icon>}
                                onClick={() =>
                                    navigate(
                                        `/eventos/${ocorrencia.ocorrenciaOrigemCancelamentoId}`
                                    )
                                }
                            >
                                Ver ocorrência cancelada
                            </Button>
                        ) : undefined,
                        <ArquivarButton
                            key="arquivar"
                            ocorrencia={ocorrencia}
                        />,
                        cancelamentoConcluido ? (
                            <Chip
                                key="estado"
                                label="Cancelada"
                                color="error"
                                variant="outlined"
                                size="small"
                                icon={<Icon>cancel</Icon>}
                            />
                        ) : (
                            <LabelEstado
                                key="estado"
                                estado={ocorrencia.estadoEnvio}
                                label={getLabelEstadoEntrega(
                                    ocorrencia,
                                    destinoEntrega
                                )}
                            />
                        ),
                        <Voltar key="voltar" />
                    ]}
                />

                {!configuracaoQuery.isError && configuracaoEntrega?.enabled && (
                    <Alert severity="info" sx={{mb: 2}}>
                        Modo de envio atual:{" "}
                        <strong>{getLabelDestino(configuracaoEntrega)}</strong>.
                        O destino registrado nesta ocorrência é preservado.
                    </Alert>
                )}

                {!cancelamentoConcluido && (
                    <DependenciaFuncionalAlert
                        ocorrencia={ocorrencia}
                        onNavigate={navigate}
                    />
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            <Resumo
                                label="Identificador"
                                valor={ocorrencia.id}
                                icone="tag"
                            />
                            <Resumo
                                label="Estratégia"
                                valor={ocorrencia.estrategiaEnvio}
                                icone="route"
                            />
                            <Resumo
                                label="Código de registro"
                                valor={ocorrencia.codigoRegistroInformacao}
                                icone="fingerprint"
                                onCopy={() =>
                                    copiar(
                                        ocorrencia.codigoRegistroInformacao,
                                        "Código de registro"
                                    )
                                }
                            />
                            <Resumo
                                label="Chave do pacote"
                                valor={ocorrencia.chavePacote}
                                icone="inventory_2"
                                onCopy={() =>
                                    copiar(
                                        ocorrencia.chavePacote,
                                        "Chave do pacote"
                                    )
                                }
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <ResumoProcessamento
                            ocorrencia={ocorrencia}
                            ocorrenciaRaiz={ocorrenciaRaiz}
                        />
                    </Grid>
                    {ocorrenciaFluxo.eventoAtual ===
                        "FOLHA_PAGAMENTO_INICIAR" && (
                        <Grid item xs={12}>
                            <ResumoFolhaCompetencia
                                competencia={
                                    getReferenciaTemporalOcorrencia(
                                        ocorrenciaFluxo
                                    ).competencia
                                }
                                ocorrenciaAtualId={ocorrenciaFluxo.id}
                            />
                        </Grid>
                    )}
                    {ehEntregaFornecedor && (
                        <FornecedorEntregaCard
                            ocorrencia={ocorrencia}
                            podeReconciliar={canReconcileFornecedor()}
                            onReconciliar={handleReconciliarFornecedor}
                            loading={reconciliarFornecedor.isLoading}
                        />
                    )}
                    {ehCancelamento ? (
                        <CancelamentoOrigemCard
                            ocorrencia={ocorrencia}
                            origem={ocorrenciaOrigemCancelamento}
                            onNavigate={(ocorrenciaId) =>
                                navigate(`/eventos/${ocorrenciaId}`)
                            }
                        />
                    ) : cancelamentoConcluido ? (
                        <CancelamentoConcluidoCard
                            cancelamento={cancelamentoConcluido}
                            onNavigate={(ocorrenciaId) =>
                                navigate(`/eventos/${ocorrenciaId}`)
                            }
                        />
                    ) : (
                        <Grid item xs={12}>
                            <Card variant="outlined" sx={{borderRadius: 2}}>
                                <CardContent>
                                    <Stack
                                        direction={{xs: "column", md: "row"}}
                                        spacing={2}
                                        alignItems={{md: "center"}}
                                    >
                                        <Box flex={1}>
                                            <Typography variant="h6">
                                                {ehEntregaFornecedor
                                                    ? "Cancelamento pelo fornecedor"
                                                    : "Exclusão no e-Sfinge"}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                A solicitação não apaga o
                                                histórico local. Ela cria uma
                                                nova ocorrência de cancelamento,
                                                que deverá ser revisada e
                                                autorizada antes da transmissão
                                                ao destino da ocorrência.
                                            </Typography>
                                        </Box>
                                        {exclusaoPorCodigoDisponivel && (
                                            <Button
                                                color="error"
                                                variant="outlined"
                                                startIcon={
                                                    <Icon>delete_outline</Icon>
                                                }
                                                onClick={handleCancelarPorCR}
                                            >
                                                Preparar pelo código de registro
                                            </Button>
                                        )}
                                        {exclusaoPorPacoteDisponivel && (
                                            <Button
                                                color="error"
                                                variant="outlined"
                                                startIcon={
                                                    <Icon>delete_sweep</Icon>
                                                }
                                                onClick={handleCancelarPorCP}
                                            >
                                                {descarteFuncionalDisponivel
                                                    ? "Preparar descarte do pacote"
                                                    : "Preparar cancelamento do fluxo"}
                                            </Button>
                                        )}
                                    </Stack>
                                    {descarteFuncionalDisponivel && (
                                        <Alert severity="warning" sx={{mt: 2}}>
                                            O pacote foi aberto, mas a tentativa
                                            terminou com erro. Arquivar não
                                            cancela o pacote no e-Sfinge.
                                            Confira a rejeição e o extrato
                                            oficial antes de autorizar o
                                            descarte. Aguarde sua confirmação
                                            antes de preparar outro envio para
                                            os mesmos vínculos.
                                        </Alert>
                                    )}
                                    {!exclusaoPorCodigoDisponivel &&
                                        !exclusaoPorPacoteDisponivel && (
                                            <Alert severity="info" sx={{mt: 2}}>
                                                {motivoExclusaoIndisponivel()}
                                            </Alert>
                                        )}
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <ErrosProcessamento ocorrencia={ocorrencia} />
                    </Grid>

                    {!cancelamentoConcluido && (
                        <AprovacaoHomologacaoCard
                            ocorrencia={ocorrencia}
                            ambiente={AMBIENTE}
                            autenticacaoDisponivel={podeAutorizarModulo(
                                ocorrencia
                            )}
                            justificativa={justificativaAprovacao}
                            onJustificativaChange={setJustificativaAprovacao}
                            referenciaRemessaContabil={
                                referenciaRemessaContabil
                            }
                            onReferenciaRemessaContabilChange={
                                setReferenciaRemessaContabil
                            }
                            onAutorizar={handleAutorizarHomologacao}
                            autorizando={autorizarHomologacao.isLoading}
                        />
                    )}

                    {!cancelamentoConcluido &&
                        ocorrencia.estadoEnvio ===
                            "RESULTADO_INDETERMINADO" && (
                            <Grid item xs={12}>
                                <Alert severity="warning">
                                    {ehEntregaFornecedor
                                        ? "O resultado do fornecedor é ambíguo. Não crie uma nova ocorrência: um usuário master deve consultar a evidência externa e reconciliar esta mesma entrega."
                                        : "O resultado externo é ambíguo e a comunicação pode ter sido recebida pelo e-Sfinge. Não reprocesse automaticamente. Consulte Serviços Gerais ou o TCE-MS, reconcilie os identificadores e registre a decisão antes de qualquer nova ação."}
                                </Alert>
                            </Grid>
                        )}

                    {secoesComplementares.length > 0 && (
                        <Grid item xs={12}>
                            <DetalhesOcorrenciaTabs
                                secoes={secoesComplementares}
                            />
                        </Grid>
                    )}
                </Grid>
            </DataPanel>
        </Page>
    );
}

export function CancelamentoConcluidoCard({cancelamento, onNavigate}) {
    return (
        <Grid item xs={12}>
            <Alert severity="info" icon={<Icon>cancel</Icon>}>
                <Stack spacing={1} alignItems="flex-start">
                    <Typography variant="h6">Cancelamento concluído</Typography>
                    <Typography variant="body2">
                        Remessa cancelada pela ocorrência #
                        {cancelamento.ocorrenciaId}. Os dados e as respostas do
                        processamento original permanecem disponíveis para
                        consulta do histórico.
                    </Typography>
                    {cancelamento.identificador && (
                        <Typography
                            variant="body2"
                            sx={{overflowWrap: "anywhere"}}
                        >
                            {cancelamento.tipoIdentificador === "CHAVE_PACOTE"
                                ? "Pacote cancelado"
                                : "Código de registro cancelado"}
                            : {cancelamento.identificador}
                        </Typography>
                    )}
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onNavigate(cancelamento.ocorrenciaId)}
                    >
                        Ver cancelamento #{cancelamento.ocorrenciaId}
                    </Button>
                </Stack>
            </Alert>
        </Grid>
    );
}

export function DependenciaFuncionalAlert({ocorrencia, onNavigate}) {
    if (!ocorrencia.ocorrenciaDependenciaId) return null;

    return (
        <Alert
            severity="info"
            sx={{mb: 2}}
            action={
                <Button
                    color="inherit"
                    onClick={() =>
                        onNavigate(
                            `/eventos/${ocorrencia.ocorrenciaDependenciaId}`
                        )
                    }
                >
                    Ver etapa anterior
                </Button>
            }
        >
            Esta remessa depende do aceite final da ocorrência #
            {ocorrencia.ocorrenciaDependenciaId} no e-Sfinge. Cada remessa exige
            sua própria autorização.
        </Alert>
    );
}

export function CancelamentoOrigemCard({ocorrencia, origem, onNavigate}) {
    const origemId = ocorrencia?.ocorrenciaOrigemCancelamentoId;
    const eventoOrigem =
        origem?.eventoAtual || ocorrencia?.eventoOrigemCancelamento;
    const codigoRegistro = ocorrencia?.dadosEnviados?.codigoRegistro;
    const chavePacote = ocorrencia?.dadosEnviados?.chavePacote;
    const identificador = codigoRegistro || chavePacote;
    const rotuloIdentificador = codigoRegistro
        ? "Código de registro"
        : "Chave do pacote";

    return (
        <Grid item xs={12}>
            <Card
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    borderLeftWidth: 4,
                    borderLeftColor: "error.main"
                }}
            >
                <CardContent>
                    <Stack spacing={2}>
                        <Stack
                            direction={{xs: "column", md: "row"}}
                            spacing={2}
                            alignItems={{md: "center"}}
                            justifyContent="space-between"
                        >
                            <Stack direction="row" spacing={1.5}>
                                <Box
                                    sx={{
                                        width: 42,
                                        height: 42,
                                        borderRadius: 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "error.main",
                                        bgcolor: "rgba(211, 47, 47, 0.08)"
                                    }}
                                >
                                    <Icon>cancel_schedule_send</Icon>
                                </Box>
                                <Box>
                                    <Typography variant="h6">
                                        Solicitação de cancelamento
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Exclusão controlada de um envio já
                                        registrado no e-Sfinge.
                                    </Typography>
                                </Box>
                            </Stack>
                            {origemId && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<Icon>open_in_new</Icon>}
                                    onClick={() => onNavigate(origemId)}
                                >
                                    Abrir ocorrência de origem
                                </Button>
                            )}
                        </Stack>

                        {origemId ? (
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={3}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Ocorrência de origem
                                    </Typography>
                                    <Typography fontWeight={600}>
                                        #{origemId}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Evento cancelado
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        sx={{overflowWrap: "anywhere"}}
                                    >
                                        {eventoOrigem || "Não informado"}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Estado da origem
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                    >
                                        {origem?.estadoEnvio || "Não informado"}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {rotuloIdentificador}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontFamily="monospace"
                                        sx={{overflowWrap: "anywhere"}}
                                    >
                                        {identificador || "Não informado"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        ) : (
                            <Alert severity="warning">
                                O histórico deste cancelamento ainda não possui
                                vínculo persistido com a ocorrência de origem.
                                Consulte o identificador técnico antes de
                                autorizar.
                            </Alert>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        </Grid>
    );
}

export function AprovacaoHomologacaoCard({
    ocorrencia,
    ambiente,
    autenticacaoDisponivel = true,
    justificativa,
    onJustificativaChange,
    referenciaRemessaContabil = "",
    onReferenciaRemessaContabilChange = () => {},
    onAutorizar,
    autorizando,
    gestaoFiscalEnvioHabilitado = GESTAO_FISCAL_ENVIO_HOMOLOGACAO_ENABLED
}) {
    if (
        ocorrencia.estadoEnvio !== ESTADO_AGUARDANDO_APROVACAO ||
        !ocorrenciaAutorizavel(ocorrencia)
    ) {
        return null;
    }

    const ambientePermitido = ambientePermiteAutorizacao(ambiente);
    const acessoInstitucionalDisponivel = Boolean(autenticacaoDisponivel);
    const arquivada = Boolean(ocorrencia.arquivado);
    const quantidadeCaracteres = justificativa.trim().length;
    const justificativaInvalida =
        quantidadeCaracteres > 0 &&
        !justificativaAprovacaoValida(justificativa);
    const exigeRemessaContabil = ocorrencia.eventoAtual === "CONTAS_BANCARIAS";
    const gestaoFiscalBloqueada =
        ocorrencia.eventoAtual === "GESTAO_FISCAL_ENVIAR" &&
        !gestaoFiscalEnvioHabilitado;
    const competenciaAnterior = competenciaContabilAnterior(
        ocorrencia.dadosEnviados?.anoMes
    );
    const referenciaRemessaInvalida =
        exigeRemessaContabil &&
        referenciaRemessaContabil?.trim().length > 0 &&
        !referenciaRemessaContabilValida(referenciaRemessaContabil);

    return (
        <Grid item xs={12}>
            <Card
                variant="outlined"
                sx={{borderRadius: 2, borderColor: "warning.main"}}
            >
                <CardContent>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="h6">
                                Aguardando autorização de envio
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Confira os dados preparados abaixo. Somente a
                                autorização explícita libera esta ocorrência
                                para a fila que poderá ser processada pelo
                                scheduler.
                            </Typography>
                        </Box>

                        {!ambientePermitido && (
                            <Alert severity="error">
                                A autorização está indisponível no ambiente{" "}
                                <strong>
                                    {ambiente || "não identificado"}
                                </strong>
                                . A autorização desta versão está disponível
                                somente em homologação ou simulação (HOM/MOCK).
                                A transmissão em produção ainda não está
                                habilitada.
                            </Alert>
                        )}

                        {ambientePermitido &&
                            !acessoInstitucionalDisponivel && (
                                <Alert severity="info">
                                    A autorização exige autenticação
                                    institucional pelo Keycloak. Neste modo é
                                    possível preparar e revisar os dados, mas a
                                    ocorrência permanecerá aguardando aprovação
                                    até ser acessada em um ambiente autenticado.
                                </Alert>
                            )}

                        {ambientePermitido &&
                            acessoInstitucionalDisponivel &&
                            arquivada && (
                                <Alert severity="info">
                                    Esta ocorrência está arquivada. Desarquive-a
                                    antes de solicitar a autorização.
                                </Alert>
                            )}

                        {ambientePermitido &&
                            acessoInstitucionalDisponivel &&
                            !arquivada && (
                                <>
                                    {gestaoFiscalBloqueada && (
                                        <Alert severity="error">
                                            A obrigação de Gestão Fiscal está
                                            mantida, mas a autorização de envio
                                            permanece bloqueada até a validação
                                            institucional do leiaute estadual
                                            aplicável à DPGE/MS com o TCE-MS.
                                        </Alert>
                                    )}
                                    <Alert severity="warning">
                                        Ao autorizar, a ocorrência passará para{" "}
                                        <strong>EM FILA</strong> e poderá ser
                                        processada no ambiente de homologação.
                                        Esta ação não autoriza produção.
                                    </Alert>
                                    {exigeRemessaContabil && (
                                        <Alert severity="info">
                                            O e-Sfinge exige a remessa de
                                            Registros Contábeis da competência
                                            anterior
                                            {competenciaAnterior
                                                ? ` (${competenciaAnterior})`
                                                : ""}
                                            . Confirme-a no TCE Virtual e
                                            registre abaixo o código ou a
                                            referência usada na conferência.
                                        </Alert>
                                    )}
                                    {exigeRemessaContabil && (
                                        <TextField
                                            fullWidth
                                            required
                                            label="Referência da remessa contábil anterior"
                                            value={referenciaRemessaContabil}
                                            onChange={(event) =>
                                                onReferenciaRemessaContabilChange(
                                                    event.target.value
                                                )
                                            }
                                            error={referenciaRemessaInvalida}
                                            helperText="Código de registro, protocolo ou referência verificável no TCE Virtual (5 a 500 caracteres)"
                                            inputProps={{maxLength: 500}}
                                        />
                                    )}
                                    <TextField
                                        fullWidth
                                        required
                                        multiline
                                        minRows={3}
                                        label="Justificativa da autorização"
                                        value={justificativa}
                                        onChange={(event) =>
                                            onJustificativaChange(
                                                event.target.value
                                            )
                                        }
                                        error={justificativaInvalida}
                                        helperText={`${quantidadeCaracteres}/500 caracteres — mínimo de 10`}
                                        inputProps={{maxLength: 500}}
                                    />
                                    <Stack
                                        direction="row"
                                        justifyContent="flex-end"
                                    >
                                        <Button
                                            variant="contained"
                                            color="warning"
                                            startIcon={
                                                <Icon>verified_user</Icon>
                                            }
                                            disabled={
                                                autorizando ||
                                                gestaoFiscalBloqueada ||
                                                !justificativaAprovacaoValida(
                                                    justificativa
                                                ) ||
                                                (exigeRemessaContabil &&
                                                    !referenciaRemessaContabilValida(
                                                        referenciaRemessaContabil
                                                    ))
                                            }
                                            onClick={onAutorizar}
                                        >
                                            {autorizando
                                                ? "Autorizando..."
                                                : "Autorizar em homologação"}
                                        </Button>
                                    </Stack>
                                </>
                            )}
                    </Stack>
                </CardContent>
            </Card>
        </Grid>
    );
}

export function AuditoriaHomologacaoCard({
    ocorrencia,
    ocorrenciaOrigemId,
    embedded = false
}) {
    if (!possuiAuditoriaHomologacao(ocorrencia)) {
        return null;
    }

    return (
        <Grid {...(embedded ? {} : {item: true, xs: 12})}>
            <Card
                variant={embedded ? "elevation" : "outlined"}
                elevation={0}
                sx={{
                    borderRadius: embedded ? 0 : 2,
                    border: embedded ? 0 : undefined,
                    borderColor: embedded ? undefined : "info.main",
                    boxShadow: "none"
                }}
            >
                <CardContent
                    sx={embedded ? {p: 0, "&:last-child": {pb: 0}} : undefined}
                >
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="h6">
                                Auditoria da autorização de envio
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {ocorrenciaOrigemId
                                    ? `Autorização registrada na ocorrência raiz #${ocorrenciaOrigemId} e válida para todas as etapas vinculadas deste fluxo.`
                                    : "Registro permanente da autorização, com o destino informado no momento da liberação."}
                            </Typography>
                        </Box>
                        <Grid container spacing={2}>
                            <AuditoriaCampo
                                label="Usuário autorizador"
                                value={
                                    ocorrencia.usuarioAutorizacaoHomologacao ||
                                    (ocorrencia.identificadorUsuarioAutorizacaoHomologacao
                                        ? "Usuário autenticado"
                                        : undefined)
                                }
                                secondaryValue={
                                    ocorrencia.identificadorUsuarioAutorizacaoHomologacao
                                        ? `Identificador imutável: ${mascararIdentificadorAuditoria(
                                              ocorrencia.identificadorUsuarioAutorizacaoHomologacao
                                          )}`
                                        : undefined
                                }
                            />
                            <AuditoriaCampo
                                label="Data e hora"
                                value={
                                    ocorrencia.dataHoraAutorizacaoHomologacao
                                        ? formataDataTempoLong(
                                              ocorrencia.dataHoraAutorizacaoHomologacao
                                          )
                                        : undefined
                                }
                            />
                            <AuditoriaCampo
                                label="Destino autorizado"
                                value={ocorrencia.destinoAutorizacaoHomologacao}
                            />
                            <AuditoriaCampo
                                label="Justificativa"
                                value={
                                    ocorrencia.justificativaAutorizacaoHomologacao
                                }
                                fullWidth
                            />
                            <AuditoriaCampo
                                label="Competência contábil anterior"
                                value={
                                    ocorrencia.competenciaRemessaContabilAnterior
                                }
                            />
                            <AuditoriaCampo
                                label="Referência da remessa contábil"
                                value={
                                    ocorrencia.referenciaRemessaContabilAnterior
                                }
                                fullWidth
                            />
                        </Grid>
                    </Stack>
                </CardContent>
            </Card>
        </Grid>
    );
}

function AuditoriaCampo({label, value, secondaryValue, fullWidth = false}) {
    if (!value) {
        return null;
    }

    return (
        <Grid item xs={12} md={fullWidth ? 12 : 4}>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
            <Typography sx={{overflowWrap: "anywhere"}}>{value}</Typography>
            {secondaryValue && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{overflowWrap: "anywhere"}}
                >
                    {secondaryValue}
                </Typography>
            )}
        </Grid>
    );
}

export function eventoFinanceiroAutorizavel(evento) {
    return EVENTOS_FINANCEIROS_AUTORIZAVEIS.has(evento);
}

export function ocorrenciaAutorizavel(ocorrencia) {
    return Boolean(
        ocorrencia?.moduloOrigem === "FOLHA" ||
            (ocorrencia?.moduloOrigem === "FINANCEIRA" &&
                eventoFinanceiroAutorizavel(ocorrencia?.eventoAtual))
    );
}

export function podeAutorizarModulo(ocorrencia) {
    if (ocorrencia?.moduloOrigem === "FOLHA") {
        return canAuthorizeFolha();
    }
    if (ocorrencia?.moduloOrigem === "FINANCEIRA") {
        return canAuthorizeFinanceira();
    }
    return false;
}

export function possuiAuditoriaHomologacao(ocorrencia) {
    return Boolean(
        ocorrencia?.usuarioAutorizacaoHomologacao ||
            ocorrencia?.identificadorUsuarioAutorizacaoHomologacao ||
            ocorrencia?.dataHoraAutorizacaoHomologacao ||
            ocorrencia?.justificativaAutorizacaoHomologacao ||
            ocorrencia?.destinoAutorizacaoHomologacao
    );
}

export function mascararIdentificadorAuditoria(identificador) {
    const valor = `${identificador || ""}`.trim();

    if (valor.length <= 12) {
        return valor;
    }

    return `${valor.slice(0, 8)}…${valor.slice(-4)}`;
}

export function getRotulosPayloadPorEstado(estado) {
    switch (estado) {
        case "AGUARDANDO_APROVACAO":
            return {
                primary: "Dados preparados",
                secondary:
                    "Revise os dados e o destino antes de autorizar o processamento"
            };
        case "EM_FILA":
            return {
                primary: "Dados autorizados e em fila",
                secondary:
                    "Dados autorizados para a fila; este estado ainda não comprova recebimento pelo destino"
            };
        case "PROCESSAMENTO":
            return {
                primary: "Dados em processamento",
                secondary:
                    "Estrutura associada ao processamento em andamento no conector"
            };
        case "PROCESSADO_COM_SUCESSO":
            return {
                primary: "Dados processados",
                secondary:
                    "Estrutura associada ao processamento concluído com sucesso"
            };
        case "PROCESSADO_COM_ERRO":
            return {
                primary: "Dados do processamento",
                secondary:
                    "Estrutura associada ao processamento concluído com erro"
            };
        case "RESULTADO_INDETERMINADO":
            return {
                primary: "Dados com resultado indeterminado",
                secondary:
                    "A comunicação pode ter ocorrido; não reprocesse automaticamente. Consulte Serviços Gerais ou o TCE-MS e reconcilie antes de uma decisão documentada"
            };
        default:
            return {
                primary: "Dados da ocorrência",
                secondary: "Estrutura registrada localmente pelo conector"
            };
    }
}

export function justificativaAprovacaoValida(justificativa) {
    const tamanho = `${justificativa || ""}`.trim().length;
    return tamanho >= 10 && tamanho <= 500;
}

export function referenciaRemessaContabilValida(referencia) {
    const tamanho = `${referencia || ""}`.trim().length;
    return tamanho >= 5 && tamanho <= 500;
}

export function competenciaContabilAnterior(anoMes) {
    if (!/^\d{6}$/.test(`${anoMes || ""}`)) {
        return null;
    }
    const ano = Number(`${anoMes}`.slice(0, 4));
    const mes = Number(`${anoMes}`.slice(4, 6));
    if (ano < 1 || mes < 1 || mes > 12) {
        return null;
    }
    const anterior = new Date(Date.UTC(ano, mes - 2, 1));
    return `${anterior.getUTCFullYear()}${`${
        anterior.getUTCMonth() + 1
    }`.padStart(2, "0")}`;
}

export function podeAutorizarOcorrencia(
    ocorrencia,
    ambiente,
    autenticacaoDisponivel = true
) {
    return Boolean(
        ocorrencia?.estadoEnvio === ESTADO_AGUARDANDO_APROVACAO &&
            !ocorrencia?.arquivado &&
            ocorrenciaAutorizavel(ocorrencia) &&
            ambientePermiteAutorizacao(ambiente) &&
            autenticacaoDisponivel
    );
}

export function isConflito(error) {
    const status = error?.status ?? error?.statusCode ?? error?.code;
    return Number(status) === 409 || error?.error === "Conflict";
}

export function podeDescartarPacoteFuncionalComErro(
    ocorrencia,
    destinoEntrega
) {
    return Boolean(
        destinoEntrega !== "FORNECEDOR" &&
            ocorrencia?.eventoAtual === "DADOS_FUNCIONAIS_ATIVOS_INICIAR" &&
            ocorrencia?.estrategiaEnvio === "FLUXO" &&
            ocorrencia?.estadoEnvio === "PROCESSADO_COM_ERRO" &&
            ocorrencia?.chavePacote?.trim() &&
            !ocorrencia?.codigoRegistroInformacao?.trim()
    );
}

export function obterDestinoEntrega(ocorrencia, ocorrenciaRaiz) {
    return (
        ocorrencia?.destinoEntrega ||
        ocorrenciaRaiz?.destinoEntrega ||
        ocorrencia?.resposta?.destino ||
        ocorrenciaRaiz?.resposta?.destino
    );
}

export function getLabelEstadoEntrega(ocorrencia, destinoEntrega) {
    if (destinoEntrega !== "FORNECEDOR") {
        return undefined;
    }

    const estado = ocorrencia?.resposta?.estadoEntregaFornecedor;
    const labels = {
        PENDENTE: "PENDENTE NO FORNECEDOR",
        PROCESSAMENTO: "PROCESSANDO ENTREGA AO FORNECEDOR",
        ACEITA_PELO_FORNECEDOR: "ACEITA PELO FORNECEDOR",
        REJEITADA_PELO_FORNECEDOR: "REJEITADA PELO FORNECEDOR",
        FALHA_ANTES_DO_ENVIO: "NÃO TRANSMITIDA AO FORNECEDOR",
        RESULTADO_INDETERMINADO: "RESULTADO DO FORNECEDOR INDETERMINADO"
    };
    return labels[estado];
}

export function getLabelDestino(configuracao) {
    return obterModoEnvio(configuracao).rotulo;
}

function Resumo({label, valor, icone, onCopy}) {
    return (
        <Grid item xs={12} sm={6} md={3} sx={{minWidth: 0}}>
            <Card variant="outlined" sx={{height: "100%", borderRadius: 2}}>
                <CardContent
                    sx={{display: "flex", gap: 1.5, alignItems: "center"}}
                >
                    <Box
                        sx={{
                            bgcolor: "action.hover",
                            p: 1,
                            borderRadius: 2,
                            display: "flex"
                        }}
                    >
                        <Icon color="primary">{icone}</Icon>
                    </Box>
                    <Box minWidth={0} flex={1}>
                        <Typography variant="caption" color="text.secondary">
                            {label}
                        </Typography>
                        <Typography
                            fontWeight={700}
                            noWrap
                            title={`${valor || "Não informado"}`}
                        >
                            {valor || "Não informado"}
                        </Typography>
                    </Box>
                    {valor && onCopy && (
                        <Tooltip title={`Copiar ${label.toLowerCase()}`}>
                            <IconButton
                                size="small"
                                aria-label={`Copiar ${label.toLowerCase()}`}
                                onClick={onCopy}
                            >
                                <Icon fontSize="small">content_copy</Icon>
                            </IconButton>
                        </Tooltip>
                    )}
                </CardContent>
            </Card>
        </Grid>
    );
}

export default EventoDetalhePage;
